import { tr, trn, appLocale } from '@/i18n';
import { localizeApiMessage } from '@/api/apiMessages';

/**
 * The API refuses sign-in, sign-up, password resets and MFA codes with English
 * sentences. The ones a visitor can meet on the public pages are shown in the
 * interface language; anything else goes through the general API-message
 * translations (apiMessages.ts) and otherwise is shown as sent.
 */

const EXACT: Record<string, () => string> = {
    // Sign-up (public registration form, legacy /auth/register)
    'An account with this email already exists.': () => tr('login.err.email_exists', 'An account with this email already exists.'),
    'User with this email already exists': () => tr('login.err.email_exists', 'An account with this email already exists.'),
    'A registration request with this email is already pending.': () =>
        tr('login.err.request_pending', 'A registration request with this email is already waiting for review.'),
    'This username is already taken.': () => tr('login.err.username_taken', 'This username is already taken.'),
    'Registration cannot be completed using this email domain.': () =>
        tr('login.err.domain_declined', 'Registration isn’t possible with this email domain.'),
    'Registration is disabled': () => tr('login.err.registration_closed', 'Registration is closed.'),
    'Self-registration is not enabled.': () => tr('login.err.registration_closed', 'Registration is closed.'),
    'Email is required': () => tr('login.validation.email_required', 'Email is required'),
    // Sign-in
    'Account is permanently locked. Please contact your administrator.': () =>
        tr('login.err.account_locked', 'This account is locked. Please contact your administrator.'),
    'Access to this organisation is currently unavailable. Contact your administrator.': () =>
        tr('login.err.tenant_unavailable', 'Access to this organisation is currently unavailable. Contact your administrator.'),
    'Sign-in is temporarily unavailable.': () => tr('login.err.signin_unavailable', 'Sign-in is temporarily unavailable.'),
    'Sign-in link is invalid or has expired.': () => tr('login.err.signin_link_invalid', 'This sign-in link is invalid or has expired.'),
    // Password reset
    'This reset link is invalid or has expired. Ask for a new one.': () =>
        tr('login.reset_link_invalid', 'This reset link is invalid or has expired. Ask for a new one.'),
    // MFA
    'Invalid or expired code.': () => tr('ui.invalid_or_expired_code', 'Invalid or expired code.'),
    'Invalid authenticator code.': () => tr('ui.invalid_code', 'Invalid code.'),
    'Could not send the email code. Contact your administrator.': () =>
        tr('login.err.email_code_failed', 'Could not send the email code. Contact your administrator.'),
    'No passkey is registered for this account.': () => tr('login.err.no_passkey', 'No passkey is registered for this account.'),
    'Passkey verification failed.': () => tr('ui.passkey_verification_failed', 'Passkey verification failed.'),
    'Passkey registration failed.': () => tr('ui.could_not_create_a_passkey', 'Could not create a passkey.'),
};

// The phrases password_policy_errors() (backend) joins into one sentence.
const passwordRule = (phrase: string): string | null => {
    const min = /^at least (\d+) characters$/.exec(phrase);
    if (min) return tr('login.pw_rule.min_length', 'at least {v0} characters', { v0: min[1] });
    if (phrase === 'a letter') return tr('login.pw_rule.letter', 'a letter');
    if (phrase === 'a number') return tr('login.pw_rule.number', 'a number');
    if (phrase === 'a special character') return tr('login.pw_rule.special', 'a special character');
    return null;
};

const joinList = (items: string[]): string => {
    try {
        // Intl.ListFormat is ES2021; the project's TS lib predates it.
        const ListFormat = (Intl as any).ListFormat;
        return new ListFormat(appLocale(), { style: 'long', type: 'conjunction' }).format(items);
    } catch {
        return items.join(', ');
    }
};

const PATTERNS: Array<[RegExp, (m: RegExpExecArray) => string | null]> = [
    [/^Account is temporarily locked due to too many failed attempts\. Try again in (\d+) minutes?\.$/, m =>
        trn('login.err.locked_minutes', Number(m[1]),
            'This account is temporarily locked after too many failed attempts. Try again in {n} minute.',
            'This account is temporarily locked after too many failed attempts. Try again in {n} minutes.')],
    [/^Password was used recently; the last (\d+) passwords cannot be reused\.$/, m =>
        tr('login.err.password_reused', 'You used this password recently. Choose one that isn’t among your last {v0} passwords.', { v0: m[1] })],
    [/^Password (?:does not meet the policy: must contain|must contain) (.+)\.$/, m => {
        const rules = m[1].split(', ').map(passwordRule);
        if (rules.some(r => r === null)) return null;
        return tr('login.err.password_policy', 'The password must contain {v0}.', { v0: joinList(rules as string[]) });
    }],
];

/** `detail` from an API refusal, in the interface language when it's a known one. */
export function localizeAuthMessage(detail: unknown): string {
    if (typeof detail !== 'string') return '';
    const text = detail.trim();
    const exact = EXACT[text];
    if (exact) return exact();
    for (const [re, render] of PATTERNS) {
        const m = re.exec(text);
        if (m) {
            const out = render(m);
            if (out) return out;
        }
    }
    // Any other fixed API message with a translation (see apiMessages.ts).
    return localizeApiMessage(text);
}
